import { m } from '@hub/messages';
import { Button, InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot, Label } from '@platform/ui';
import { useEffect, useRef, useState } from 'react';

interface EmailCodeFormProps {
  email: string;
  error: string | null;
  setError: (e: string | null) => void;
  onBack: () => void;
}

export function EmailCodeForm({ email, error, setError, onBack }: EmailCodeFormProps) {
  const [code, setCode] = useState('');
  const [isPending, setIsPending] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  function handleSubmit(e: React.SubmitEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (code.length < 6) {
      setError(m.auth_mfa_error_code_required());
      return;
    }
    // TODO: implement when backend is ready
    setIsPending(true);
    timeoutRef.current = setTimeout(() => {
      setIsPending(false);
      setError(m.auth_mfa_error_invalid_code());
    }, 1000);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-muted-foreground text-sm">{m.auth_mfa_email_code_sent({ email })}</p>

      <div className="mb-6 space-y-3">
        <Label className="sr-only">{m.auth_mfa_email_code_label()}</Label>
        <InputOTP
          maxLength={6}
          value={code}
          onChange={setCode}
          disabled={isPending}
          aria-invalid={!!error}
          aria-describedby={error ? 'mfa-error' : undefined}
          containerClassName="justify-center"
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
          </InputOTPGroup>
          <InputOTPSeparator />
          <InputOTPGroup>
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
      </div>

      {error != null && (
        <p id="mfa-error" className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}

      <div className="text-center">
        <button type="button" className="text-primary text-sm underline" onClick={() => setError(null)}>
          {m.auth_mfa_email_code_resend()}
        </button>
      </div>

      <div className="flex gap-3">
        <Button type="button" variant="outline" className="flex-1" onClick={onBack} disabled={isPending}>
          {m.auth_mfa_back_to_selection()}
        </Button>
        <Button type="submit" className="flex-1" loading={isPending}>
          {m.auth_mfa_submit_button()}
        </Button>
      </div>
    </form>
  );
}
